import React, { CSSProperties, useMemo } from 'react';
import clsx from 'clsx';

import {
  Animation,
  AnimationMerge,
  AnimationMove,
  AnimationNew,
  AnimationType,
} from '../types/Animations';
import { Direction } from '../types/Direction';
import { animationDuration, gridGap } from '../config';

export interface BoardTileProps {
  value: number;
  animations?: Animation[];
}

function tileTranslate(axis: 'X' | 'Y', value: number) {
  return `translate${axis}(calc(${value} * (${gridGap} + 100%))`;
}

function findAnimation<T extends Animation>(
  animations: Animation[] | undefined,
  type: AnimationType
): T {
  return animations?.find(animation => animation.type === type) as T;
}

function valueTranslate(value: number) {
  // const names = ["2", "4", "8", "16", "32", "64", "128", "256", "512", "1024", "2048", "4096", "8192"];
  const index = Math.log2(value);
  // 科学 - 9 工业 - 10(难度可达调整) 互联网 - 11 -2048 元宇宙 - 13 - 8192
  const names_zh = ["比特", "原子", "分子", "基因", "细胞", "物种", "语言", "文化", "科学", "工业", "互联网", "区块链", "元宇宙", "宏量子", "比特", "原子"];
  // const names_en = ["Bit", " Blockchain", "Molecule", "Gene", "Cell", "Species", "Language", "Culture", "Science", "Industry", "Internet", "Blockchain", "Metaverse", "Quantum", "Bit", "Atom"];
  return names_zh[index];
}

const BoardTile: React.FC<BoardTileProps> = ({ value, animations }) => {
  const moveAnimation = useMemo(
    () => findAnimation<AnimationMove>(animations, AnimationType.MOVE),
    [animations]
  );
  const newAnimation = useMemo(
    () => findAnimation<AnimationNew>(animations, AnimationType.NEW),
    [animations]
  );
  const mergeAnimation = useMemo(
    () => findAnimation<AnimationMerge>(animations, AnimationType.MERGE),
    [animations]
  );

  const style = useMemo(() => {
    if (!moveAnimation) {
      return {};
    }

    const value: CSSProperties = {
      transition: animationDuration + 'ms ease-in-out all',
    };

    switch (moveAnimation.direction) {
      case Direction.UP:
        value.transform = tileTranslate('Y', -1 * moveAnimation.value);
        break;
      case Direction.DOWN:
        value.transform = tileTranslate('Y', moveAnimation.value);
        break;
      case Direction.LEFT:
        value.transform = tileTranslate('X', -1 * moveAnimation.value);
        break;
      case Direction.RIGHT:
        value.transform = tileTranslate('X', moveAnimation.value);
        break;
    }

    return value;
  }, [moveAnimation]);

  const name = valueTranslate(value)

  return (
    <div className="board-tile">
      {value !== 0 && (
        <div
          className={clsx('board-tile-value', 'board-tile-' + value, {
            'board-tile-new': !!newAnimation,
            'board-tile-merge': !!mergeAnimation,
          })}
          style={style}
        >
          {name}
        </div>
      )}
    </div>
  );
};

export default BoardTile;
